import React, { useEffect, useMemo, useRef, useState } from "react";

/**
 * FloatingPanel (JavaScript)
 * Features:
 * - Unstyled button-like div (you style it)
 * - Animated open/close (Tailwind transitions)
 * - Close button inside panel
 * - Click outside to close
 * - Draggable panel (mouse + touch)
 *
 * Notes:
 * - Dragging uses translate(x,y) on top of absolute positioning.
 * - The header area acts as the drag handle (including the title area).
 */

export default function CCFloatingPanel({
  buttonContent = "AI Editor",
  children,
  title = "AI Editor",
  panelWidth = "w-[440px]",
  panelPosition = "right-0 mt-2",
  defaultOpen = false,
}) {
  const [open, setOpen] = useState(!!defaultOpen);
  const [mounted, setMounted] = useState(!!defaultOpen);

  // Drag state
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const draggingRef = useRef(false);
  const startRef = useRef({
    pointerX: 0,
    pointerY: 0,
    startX: 0,
    startY: 0,
  });

  const panelRef = useRef(null);

  // ---- Open/close animation helpers ----
  // We keep the element mounted slightly longer to allow close animation.
  useEffect(() => {
    if (open) {
      setMounted(true);
      return;
    }

    if (!mounted) return;
    const t = window.setTimeout(() => setMounted(false), 180); // match duration-200-ish
    return () => window.clearTimeout(t);
  }, [open, mounted]);

  // Close on outside click
  useEffect(() => {
    function handleClickOutside(e) {
      if (!open) return;
      if (!panelRef.current) return;
      if (!panelRef.current.contains(e.target)) {
        setOpen(false);
      }
    }

    if (open) document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [open]);

  // ---- Drag handlers (mouse + touch) ----
  const beginDrag = (clientX, clientY) => {
    draggingRef.current = true;
    startRef.current = {
      pointerX: clientX,
      pointerY: clientY,
      startX: offset.x,
      startY: offset.y,
    };
  };

  const updateDrag = (clientX, clientY) => {
    if (!draggingRef.current) return;
    const dx = clientX - startRef.current.pointerX;
    const dy = clientY - startRef.current.pointerY;
    setOffset({ x: startRef.current.startX + dx, y: startRef.current.startY + dy });
  };

  const endDrag = () => {
    draggingRef.current = false;
  };

  useEffect(() => {
    // Use window listeners so drag continues even if pointer leaves the handle.
    function onMouseMove(e) {
      updateDrag(e.clientX, e.clientY);
    }
    function onMouseUp() {
      endDrag();
    }
    function onTouchMove(e) {
      if (!e.touches || e.touches.length === 0) return;
      const t = e.touches[0];
      updateDrag(t.clientX, t.clientY);
    }
    function onTouchEnd() {
      endDrag();
    }

    if (mounted) {
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
      window.addEventListener("touchmove", onTouchMove, { passive: false });
      window.addEventListener("touchend", onTouchEnd);
      window.addEventListener("touchcancel", onTouchEnd);
    }

    return () => {
      window.removeEventListener("mousemove", onMouseMove);
      window.removeEventListener("mouseup", onMouseUp);
      window.removeEventListener("touchmove", onTouchMove);
      window.removeEventListener("touchend", onTouchEnd);
      window.removeEventListener("touchcancel", onTouchEnd);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mounted, offset.x, offset.y]);

  const transformStyle = useMemo(
    () => ({
      transform: `translate(${offset.x}px, ${offset.y}px)`,
    }),
    [offset.x, offset.y]
  );

  // Panel animation classes
  const animClass = open
    ? "opacity-100 scale-100 translate-y-0"
    : "opacity-0 scale-[0.98] -translate-y-1 pointer-events-none";

  return (
    <div className="relative inline-block">
      {/* Button-like div (no styling intentionally) */}
      <div onClick={() => setOpen(true)} className="bg-ccColorSecondaryPink text-white font-poppins text-lg font-semibold px-2 py-1 rounded cursor-pointer hover:bg-ccColorSecondaryPurple">
        {buttonContent}
      </div>

      {/* Floating Panel */}
      {mounted && (
        <div
          ref={panelRef}
          style={transformStyle}
          className={`absolute ${panelPosition} ${panelWidth}
            rounded-2xl
            border border-gray-200
            bg-white
            shadow-xl
            z-50
            origin-top-right
            transition-all duration-200 ease-out
            ${animClass}
          `}
        >
          {/* Header / Drag handle */}
          <div
            className="flex items-center justify-between gap-3 px-4 pt-4 pb-3 select-none cursor-move bg-slate-200"
            onMouseDown={(e) => {
              // left click only
              if (e.button !== 0) return;
              e.preventDefault();
              beginDrag(e.clientX, e.clientY);
            }}
            onTouchStart={(e) => {
              if (!e.touches || e.touches.length === 0) return;
              const t = e.touches[0];
              beginDrag(t.clientX, t.clientY);
            }}
          >
            <div className="text-sm font-medium text-gray-900">{title}</div>

            <button
              type="button"
              className="inline-flex items-center justify-center rounded-lg border border-gray-200 bg-white px-2 py-1 text-sm text-gray-700 hover:bg-gray-50"
              onClick={() => setOpen(false)}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          <div className="px-4 pb-4">
            {children || (
              <div className="text-sm text-gray-600">Floating content</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
